<script setup>
  /* global defineProps */
  import { computed } from 'vue'
  import { formatReplayLanguage } from './replayLabels'

  const props = defineProps({
    outputs: { type: Array, default: () => [] },
  })

  const warningTitles = {
    SOURCE_AUDIO_LEADING_SILENCE_PADDED: '原声开头补静音',
    SOURCE_AUDIO_TRAILING_SILENCE_PADDED: '原声末尾补静音',
    TTS_TIMELINE_SILENCE_PADDED: '翻译语音时间段补静音',
    LONG_SILENCE_RETAINED: '检测到长时间静音',
    FULL_SILENT_AUDIO_RETAINED: '音轨全程静音',
    TTS_SPEED_ADJUSTED: '翻译语音已加速',
    PPT_TRANSLATION_FALLBACK_ORIGINAL: '翻译幻灯片回退原图',
    PPT_IMAGE_FALLBACK_BLANK: '幻灯片回退空白背景',
    PPT_EMBEDDED_VIDEO_FALLBACK_STATIC: '幻灯片内视频回退静态画面',
    PPT_LAST_FRAME_EXTENDED: '最后一帧画面已延长',
    AV_DURATION_MISMATCH_RETAINED: '音视频尾部时长不一致',
    CAPTURE_DURATION_MISMATCH_RETAINED: '成片与采集窗口时长不一致',
    QUALITY_ANALYSIS_INCOMPLETE: '质量明细分析不完整',
    QUALITY_WARNING_SUMMARY_TRUNCATED: '部分异常明细已汇总',
  }

  const completedOutputs = computed(() =>
    (Array.isArray(props.outputs) ? props.outputs : [])
      .filter((output) => ['COMPLETED', 'AVAILABLE'].includes(output?.status))
      .map((output, index) => ({
        ...output,
        warningListKey: output.id == null ? `${output.audioType || 'output'}-${output.language || index}` : output.id,
      }))
  )

  const outputLabel = (output) =>
    output.audioType === 'ORIGINAL'
      ? '原声版本'
      : `${formatReplayLanguage(output.language || output.languageCode, '翻译')} 版本`

  const formatOffset = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.floor(Number(milliseconds) / 1000))
    if (!Number.isFinite(totalSeconds)) return '—'
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':')
  }

  const rangeLabel = (warning) => {
    if (warning?.startOffsetMs == null || warning?.endOffsetMs == null) return '影响整个版本'
    return `${formatOffset(warning.startOffsetMs)} — ${formatOffset(warning.endOffsetMs)}`
  }

  const warningTitle = (warning) => warningTitles[warning?.code] || '视频兼容处理'

  const detailsLabel = (warning) => {
    const details = warning?.details || {}
    const items = []
    if (Array.isArray(details.pageNumbers) && details.pageNumbers.length) {
      items.push(`PPT 第 ${details.pageNumbers.join('、')} 页`)
    }
    const tempo = Number(details.tempo)
    if (Number.isFinite(tempo) && tempo > 0) items.push(`语速 ${tempo.toFixed(2)}x`)
    const driftMs = Number(details.driftMs)
    if (Number.isFinite(driftMs) && driftMs > 0) items.push(`时长偏差 ${(driftMs / 1000).toFixed(1)} 秒`)
    return items.join(' · ')
  }
</script>

<template>
  <section v-if="completedOutputs.length" aria-label="成片质量异常清单" class="quality-section">
    <div class="quality-heading">
      <div>
        <span>QUALITY NOTICE</span>
        <strong>成片异常说明</strong>
      </div>
      <small>仅列出明显影响体验的兼容处理</small>
    </div>

    <div class="quality-groups">
      <article v-for="output in completedOutputs" :key="output.warningListKey" class="quality-group">
        <header class="group-heading">
          <strong>{{ outputLabel(output) }}</strong>
          <span v-if="Array.isArray(output.qualityWarnings) && output.qualityWarnings.length">
            {{ output.qualityWarnings.length }} 项
          </span>
        </header>

        <div v-if="output.qualityWarnings == null" class="quality-state historical">该版本生成时尚未记录质量明细</div>
        <div v-else-if="output.qualityWarnings.length === 0" class="quality-state clean">未检测到明显异常</div>
        <ol v-else class="warning-list">
          <li
            v-for="(warning, index) in output.qualityWarnings"
            :key="`${warning.code || 'warning'}-${warning.startOffsetMs ?? 'all'}-${index}`"
            class="warning-card"
          >
            <div class="warning-title">
              <strong>{{ warningTitle(warning) }}</strong>
              <span>{{ rangeLabel(warning) }}</span>
            </div>
            <p>{{ warning.message || '该时间段已采用兼容方式完成视频生成。' }}</p>
            <small v-if="detailsLabel(warning)">{{ detailsLabel(warning) }}</small>
            <code>{{ warning.code || 'QUALITY_WARNING' }}</code>
          </li>
        </ol>
      </article>
    </div>
  </section>
</template>

<style lang="scss" scoped>
  .quality-section {
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid #ebeef5;
  }

  .quality-heading,
  .group-heading,
  .warning-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .quality-heading {
    align-items: flex-end;

    div {
      display: flex;
      flex-direction: column;
      gap: 3px;
    }

    span {
      color: #d97706;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.14em;
    }

    strong {
      color: #303133;
      font-size: 14px;
    }

    small {
      max-width: 145px;
      color: #909399;
      font-size: 10px;
      line-height: 1.4;
      text-align: right;
    }
  }

  .quality-groups {
    display: grid;
    max-height: 440px;
    margin-top: 12px;
    padding-right: 4px;
    overflow-y: auto;
    gap: 12px;
  }

  .quality-group {
    min-width: 0;
  }

  .group-heading {
    margin-bottom: 7px;

    strong {
      color: #606266;
      font-size: 12px;
    }

    span {
      color: #b45309;
      font-size: 10px;
    }
  }

  .quality-state {
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 11px;
    line-height: 1.5;
  }

  .clean {
    color: #3b7a57;
    background: #f0f9f4;
    border: 1px solid #d3ebdc;
  }

  .historical {
    color: #7b8493;
    background: #f7f8fa;
    border: 1px solid #e4e7ed;
  }

  .warning-list {
    display: grid;
    margin: 0;
    padding: 0;
    list-style: none;
    gap: 7px;
  }

  .warning-card {
    display: grid;
    padding: 10px 11px;
    color: #7c4a03;
    background: #fff8eb;
    border: 1px solid #f3d6a4;
    border-radius: 6px;
    gap: 5px;

    p {
      margin: 0;
      color: #8a5d12;
      font-size: 11px;
      line-height: 1.5;
      overflow-wrap: anywhere;
    }

    small {
      color: #9a6d27;
      font-size: 10px;
    }

    code {
      color: #a27027;
      font-family: 'SFMono-Regular', Consolas, monospace;
      font-size: 9px;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }
  }

  .warning-title {
    align-items: flex-start;

    strong {
      color: #a35c00;
      font-size: 11px;
      line-height: 1.4;
    }

    span {
      flex: none;
      color: #a27027;
      font-family: 'SFMono-Regular', Consolas, monospace;
      font-size: 9px;
      white-space: nowrap;
    }
  }
</style>
